import { DateTime } from 'luxon';

export const dateDisplayOptions: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
};

export const timeDisplayOptions: Intl.DateTimeFormatOptions = {
  timeZoneName: 'shortGeneric',
  hour: 'numeric',
  minute: 'numeric',
};

export function asTimeString(
  date: Date,
  selectedTimezone?: string,
  locales?: Intl.LocalesArgument,
  options?: { removeTimeZone: boolean }
): string {
  const extraOptions = options?.removeTimeZone
    ? {
        timeZone: undefined,
      }
    : {};

  return date.toLocaleString(locales, {
    ...timeDisplayOptions,
    ...extraOptions,
    timeZone: selectedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
}

export function asDateString(
  date: Date,
  selectedTimezone?: string,
  locales?: Intl.LocalesArgument
): string {
  return date.toLocaleDateString(locales, { ...dateDisplayOptions, timeZone: selectedTimezone });
}

export function asDateTimeString(
  date: Date,
  selectedTimezone?: string,
  locales?: Intl.LocalesArgument
): string {
  return date.toLocaleTimeString(locales, { ...dateDisplayOptions, timeZone: selectedTimezone });
}

// Short date format for table columns (e.g., "Jan 21, 2026")
const shortDateOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

export function formatShiftDate(startDateTime?: Date | string): string {
  if (!startDateTime) return '';
  const date = typeof startDateTime === 'string' ? new Date(startDateTime) : startDateTime;
  return date.toLocaleDateString(undefined, shortDateOptions);
}

export function formatShiftTime(startDateTime?: Date | string): string {
  if (!startDateTime) return '';
  const date = typeof startDateTime === 'string' ? new Date(startDateTime) : startDateTime;
  return asTimeString(date, undefined, undefined, { removeTimeZone: true });
}

/** Extracts the YYYY-MM-DD portion from a Date, or returns '' if invalid/undefined. */
export function toISODateString(d: Date | undefined, timezone: string): string {
  if (!d || isNaN(d.getTime())) return '';
  const timezoneDate = DateTime.fromJSDate(d, { zone: timezone });
  if (!timezoneDate.isValid) return '';
  const year = timezoneDate.year;
  const month = String(timezoneDate.month).padStart(2, '0');
  const day = String(timezoneDate.day).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Extracts timezone HH:mm from a Date, or empty string if invalid/undefined. */
export function toTimezoneTimeString(d: Date | undefined, timezone: string): string {
  if (!d || isNaN(d.getTime())) return '';
  const timezoneDate = DateTime.fromJSDate(d, { zone: timezone });
  if (!timezoneDate.isValid) return '';
  const h = String(timezoneDate.hour).padStart(2, '0');
  const m = String(timezoneDate.minute).padStart(2, '0');
  if (!h || !m) {
    return '';
  }
  return `${h}:${m}`;
}

/** Checks whether a string is a complete HH:MM time value. */
export function isCompleteTime(v: string): boolean {
  return /^\d{2}:\d{2}$/.test(v);
}

/** Combines a YYYY-MM-DD date string and HH:mm time string into a Date with the given timezone, or undefined if invalid. */
export function buildDateTimeFromParts(
  dateStr: string,
  timeStr: string,
  timezone: string
): Date | undefined {
  if (!dateStr || !timeStr) return undefined;
  const d = DateTime.fromISO(`${dateStr}T${timeStr}`, { zone: timezone });
  if (!d.isValid) return undefined;
  return d.toJSDate();
}
